import { useEffect, useState } from 'react';
import { Card, Button, Typography, Tag, Space, Alert, Descriptions, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../api/client';

interface BillingStatus {
  billingEnabled: boolean;
  planCode: string;
  status: string;
  hasPaymentMethod: boolean;
  hasActiveSubscription: boolean;
  trialEndsAt: string | null;
  currentPeriodEndsAt: string | null;
  cancelAt: string | null;
  isApiAccessAllowed: boolean;
  isComplimentary?: boolean;
}

function statusTag(status: string) {
  // Human-friendly labels for the subscription state. CREA's terms use opaque
  // names like awaiting_feed — surface those as "pending feed" so a brand-new
  // customer doesn't read it as an error.
  const color =
    status === 'active' ? 'green'
    : status === 'trialing' ? 'blue'
    : status === 'past_due' ? 'orange'
    : status === 'awaiting_feed' ? 'default'
    : status === 'complimentary' ? 'cyan'
    : 'red';
  const label =
    status === 'awaiting_feed' ? 'pending feed'
    : status.replace('_', ' ');
  return <Tag color={color}>{label}</Tag>;
}

function accessTag(status: BillingStatus) {
  if (status.isApiAccessAllowed) {
    return <Tag color="green">Enabled</Tag>;
  }

  // For brand-new accounts the absence of a feed (not a billing problem)
  // is what's blocking access. Don't show "Suspended" — that's reserved for
  // genuine billing failures (past_due that aged out, cancelled subs).
  if (status.status === 'awaiting_feed') {
    return <Tag color="default">Activates on feed connect</Tag>;
  }

  return <Tag color="red">Suspended</Tag>;
}

export function Billing() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);

  async function load() {
    setLoading(true);
    try {
      setStatus(await apiGet<BillingStatus>('/portal/billing/status'));
    } catch (err) {
      message.error(`Failed to load billing status: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function onAddPayment() {
    setActing(true);
    try {
      const { url } = await apiPost<{ url: string }>('/portal/billing/checkout', {});
      window.location.href = url;
    } catch (err) {
      message.error(`Failed to start Checkout: ${(err as Error).message}`);
      setActing(false);
    }
  }

  async function onOpenPortal() {
    setActing(true);
    try {
      const { url } = await apiPost<{ url: string }>('/portal/billing/customer-portal', {});
      window.location.href = url;
    } catch (err) {
      message.error(`Failed to open Stripe portal: ${(err as Error).message}`);
      setActing(false);
    }
  }

  if (loading || !status) {
    return <Card loading={loading}>Loading...</Card>;
  }

  // Three distinct states with three distinct CTAs:
  //   1. Billing turned off env-wide   -> blue info banner, no actions
  //   2. Account is on the comp list   -> green success banner, no actions
  //   3. Active billing, no card yet   -> yellow warning banner + Stripe Checkout button
  // We rely on the server's explicit `billingEnabled` and `isComplimentary` flags
  // here rather than inferring from sub status — the old inference broke when we
  // flipped BillingEnabled to true.
  const billingDisabled = !status.billingEnabled;
  const isComplimentary = !!status.isComplimentary;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3}>Billing</Typography.Title>

      {billingDisabled && (
        <Alert
          type="info"
          showIcon
          message="Billing is currently disabled"
          description="You have full API access while we're in pre-launch. We'll let you know before activating subscriptions."
        />
      )}

      {!billingDisabled && isComplimentary && (
        <Alert
          type="success"
          showIcon
          message="Complimentary access"
          description="This account has full API access with no Stripe subscription. No payment method required."
        />
      )}

      {!billingDisabled && !isComplimentary && !status.hasPaymentMethod && (
        <Alert
          type="warning"
          showIcon
          message="No payment method on file"
          description="Add a card to start your 7-day free trial. You won't be charged today — the trial begins when you connect your first CREA DDF feed and you have 7 days before the first invoice. Cancel any time."
          action={<Button type="primary" onClick={onAddPayment} loading={acting}>Add payment method</Button>}
        />
      )}

      {!billingDisabled && !isComplimentary && status.hasPaymentMethod && status.status === 'awaiting_feed' && (
        <Alert
          type="success"
          showIcon
          message="Card on file — you have not been charged yet"
          description="Your 7-day free trial starts when you connect your first CREA DDF feed. After day 7 the subscription becomes active and you'll be charged $99 CAD/mo until you cancel."
          action={<Button type="primary" onClick={() => navigate('/feeds')}>Connect a feed</Button>}
        />
      )}

      {status.status === 'past_due' && (
        <Alert type="error" showIcon message="Payment failed — please update your card" description="Stripe is retrying. Open the customer portal to fix your payment method." />
      )}

      <Card title="Subscription">
        <Descriptions column={1} colon={false}>
          <Descriptions.Item label="Plan"><Tag>Basic — $99 CAD/mo</Tag></Descriptions.Item>
          <Descriptions.Item label="Status">{statusTag(status.status)}</Descriptions.Item>
          <Descriptions.Item label="API access">{accessTag(status)}</Descriptions.Item>
          {status.trialEndsAt && (
            <Descriptions.Item label="Trial ends">{new Date(status.trialEndsAt).toLocaleString()}</Descriptions.Item>
          )}
          {status.currentPeriodEndsAt && (
            <Descriptions.Item label="Renews">{new Date(status.currentPeriodEndsAt).toLocaleString()}</Descriptions.Item>
          )}
          {status.cancelAt && (
            <Descriptions.Item label="Cancels"><Typography.Text type="warning">{new Date(status.cancelAt).toLocaleString()}</Typography.Text></Descriptions.Item>
          )}
        </Descriptions>

        <Space style={{ marginTop: 16 }}>
          {status.hasPaymentMethod && (
            <Button onClick={onOpenPortal} loading={acting}>Manage billing in Stripe</Button>
          )}
        </Space>
      </Card>
    </Space>
  );
}
